import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { LayoutHeader } from "@/Header";
import { SequentialAnimation } from "@/SequentialAnimation";
import { ValidateInput } from "@/Input";
import { Button } from "@/Button";
import { Dialog } from "@/Dialog";
import {
  useBusinessMypageInfo,
  useUpdateBusinessMypageInfo,
} from "~/services/business/myPage";
import {
  formatPhoneNumber,
  formatBusinessNumber,
  // formatPhoneInput,
  formatBusinessInput,
} from "../../../utils/dataTransform";
import { useBusinessWithdrawal } from "~/services/auth";
import { useStorage } from "^/common/useStorage";
import { SignType } from "#/unique/domain";
import { signOutForOAuth, unlinkForOAuth, setForceRelogin } from "#/auth/signIn";
import { usePlatform } from "^/common/usePlatform";

export const Route = createFileRoute("/business/layout/user-owner-info")({
  component: RouteComponent,
});

interface BusinessOwnerInfoForm {
  bizUserSeq: number;
  email: string;
  bizUserName: string;
  nickname: string;
  phone: string;
  companyName: string;
  businessNumber: string;
}

function RouteComponent() {
  const { history } = useRouter();
  const { data: businessInfo, isLoading } = useBusinessMypageInfo();
  const updateMutation = useUpdateBusinessMypageInfo();

  // 모달 상태 관리
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState({
    title: "알림",
    message1: "",
    message2: "",
    message3: "",
  });

  // 회원탈퇴 상태 관리
  const [isWithdrawalConfirmVisible, setIsWithdrawalConfirmVisible] =
    useState(false);
  const [isWithdrawalCompleteVisible, setIsWithdrawalCompleteVisible] =
    useState(false);
  const [isWithdrawalErrorVisible, setIsWithdrawalErrorVisible] =
    useState(false);
  const [withdrawalErrorMessage, setWithdrawalErrorMessage] = useState("");
  const [sns] = useStorage<SignType>("local-sign-sns-type");
  const { mutate: withdrawal } = useBusinessWithdrawal();
  const { isApp, isAppIOS, isAppAndroid } = usePlatform();

  const showModal = (
    message1: string,
    message2: string = "",
    message3: string = "",
    title: string = "알림"
  ) => {
    setModalMessage({ title, message1, message2, message3 });
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
  };

  const handleWithdrawalConfirmOpen = () => {
    setIsWithdrawalConfirmVisible(true);
  };

  const handleWithdrawalConfirmClose = () => {
    setIsWithdrawalConfirmVisible(false);
  };

  const handleWithdrawalCompleteClose = () => {
    setIsWithdrawalCompleteVisible(false);
    // 회원탈퇴 후: SNS 권한까지 끊기 (단순 로그아웃 X)
    handleAfterWithdrawal();
  };

  const handleWithdrawalErrorClose = () => {
    setIsWithdrawalErrorVisible(false);
    setWithdrawalErrorMessage("");
  };

  const handleWithdrawalConfirm = () => {
    setIsWithdrawalConfirmVisible(false);

    withdrawal(undefined, {
      onSuccess: (response) => {
        console.log("회원탈퇴 응답:", response);
        if (response.statusCode === 200) {
          setIsWithdrawalCompleteVisible(true);
        } else {
          const errorMessage =
            response.message || "회원 탈퇴 처리 중 오류가 발생했습니다.";
          setWithdrawalErrorMessage(errorMessage);
          setIsWithdrawalErrorVisible(true);
        }
      },
      onError: (error: any) => {
        console.error("회원탈퇴 실패:", error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "회원 탈퇴 처리 중 오류가 발생했습니다.";
        setWithdrawalErrorMessage(errorMessage);
        setIsWithdrawalErrorVisible(true);
      },
    });
  };

  /** 로컬 토큰/세션 정리 — 웹·앱 공통 */
  const clearLocalSession = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("local-sns-id");
    localStorage.removeItem("local-sign-sns-type");
    localStorage.removeItem("session-fcm");
    sessionStorage.removeItem("session-sign-sns-type");
  };

  /** SNS 타입을 앱 브릿지용 소문자로 변환 */
  const getSnsTypeForBridge = () => {
    if (sns === "SNS_TYPE_KAKAO") return "kakao";
    if (sns === "SNS_TYPE_GOOGLE") return "google";
    if (sns === "SNS_TYPE_APPLE") return "apple";
    if (sns === "SNS_TYPE_NAVER") return "naver";
    return "";
  };

  /** 일반 로그아웃 — 웹/앱 분리 */
  const handleLogout = async () => {
    const snsType = getSnsTypeForBridge();
    console.log("[business/owner-info] 로그아웃", { sns, snsType, isApp });

    if (isApp) {
      if (isAppAndroid) {
        window.mung.appLoginOut(snsType);
      } else if (isAppIOS) {
        window.webkit?.messageHandlers?.mung?.postMessage([
          "appLoginOut",
          snsType,
        ]);
      }
    } else {
      // 웹: SNS OAuth 로그아웃
      try {
        await signOutForOAuth();
      } catch (error) {
        console.error("OAuth 로그아웃 실패:", error);
      }
    }

    clearLocalSession();
    setForceRelogin(); // 다음 로그인 시 SNS 로그인 화면 강제 표시
    history.push("/");
  };

  /** 회원탈퇴 후 처리 — SNS 권한 자체를 끊음 (revoke/unlink) */
  const handleAfterWithdrawal = async () => {
    const snsType = getSnsTypeForBridge();
    console.log("[business/owner-info] 회원탈퇴 후 SNS 연결 끊기", { sns, snsType, isApp });

    if (isApp) {
      if (isAppAndroid && typeof window.mung?.appUnlink === "function") {
        window.mung.appUnlink(snsType);
      } else if (isAppAndroid) {
        window.mung.appLoginOut(snsType);
      } else if (isAppIOS) {
        window.webkit?.messageHandlers?.mung?.postMessage([
          "appUnlink",
          snsType,
        ]);
      }
    } else {
      try {
        await unlinkForOAuth();
      } catch (error) {
        console.error("SNS 연결 끊기 실패:", error);
      }
    }

    clearLocalSession();
    history.push("/");
  };

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
  } = useForm<BusinessOwnerInfoForm>({
    defaultValues: {},
  });

  // API 데이터를 폼에 설정
  useEffect(() => {
    if (businessInfo) {
      setValue("bizUserSeq", businessInfo.bizUserSeq);
      setValue("email", businessInfo.email);
      setValue("bizUserName", businessInfo.bizUserName);
      setValue("phone", formatPhoneNumber(businessInfo.phoneNumber));
      setValue("companyName", businessInfo.companyName);
      setValue(
        "businessNumber",
        formatBusinessNumber(businessInfo.businessNumber)
      );
    }
  }, [businessInfo, setValue]);

  const bindSubmit = async (data: BusinessOwnerInfoForm) => {
    try {
      const updateData = {
        bizUserSeq: data.bizUserSeq,
        bizUserName: data.bizUserName,
        phoneNumber: data.phone.replace(/-/g, ""),
        companyName: data.companyName,
      };

      await updateMutation.mutateAsync(updateData);
      showModal("정보가 성공적으로 수정되었습니다.", "", "", "저장 완료");
    } catch (error) {
      console.error("정보 수정 중 오류가 발생했습니다:", error);
      showModal(
        "정보 수정 중 오류가 발생했습니다.",
        "다시 시도해주세요.",
        "",
        "오류"
      );
    }
  };

  if (isLoading) {
    return (
      <>
        <LayoutHeader title={"계정정보 변경"} hasBackBtn hasFootPrint={false} />
        <div className="flex flex-auto justify-center items-center">
          <div>로딩 중...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <LayoutHeader title={"계정정보 변경"} hasBackBtn hasFootPrint={false} />
      {/* 수신자 */}
      <div className="flex-auto flex flex-col bg-[#F5F5F5]">
        <form className="flex flex-col flex-auto gap-4 px-5 pt-5 rounded-t-3xl">
          <SequentialAnimation>
            <div className="text-[16px] font-semibold">계정 설정</div>
            <ValidateInput
              className="w-full"
              placeholder="이메일 주소를 입력하세요"
              label="이메일 주소"
              disabled
              {...register("email")}
            />
            {/* <Divider className="mt-auto" /> */}
            <ValidateInput
              className="w-full"
              label="이름"
              placeholder="이름을 입력하세요"
              errorMessage={errors?.bizUserName?.message}
              {...register("bizUserName", {
                required: { value: true, message: "이름을 입력하세요" },
                minLength: {
                  value: 2,
                  message: "이름은 최소 2글자 이상이어야 합니다",
                },
                pattern: {
                  value: /^[가-힣]+$/,
                  message: "한글만 입력 가능합니다",
                },
              })}
            />
            <ValidateInput
              className="w-full"
              label="휴대폰번호"
              type="tel"
              placeholder="휴대폰 번호를 입력하세요"
              errorMessage={errors?.phone?.message}
              {...register("phone", {
                required: {
                  value: true,
                  message: "휴대폰 번호를 입력하세요",
                },
                pattern: {
                  value: /^\d{3}-\d{3,4}-\d{4}$/,
                  message: "올바른 휴대폰 번호를 입력하세요",
                },
                // onChange: (e) => {
                //   const formattedValue = formatPhoneInput(e.target.value);
                //   setValue("phone", formattedValue);
                // },
              })}
            />

            <ValidateInput
              className="w-full"
              label="상호명"
              disabled
              placeholder="상호명을 입력하세요."
              errorMessage={errors?.companyName?.message}
              {...register("companyName", {
                required: { value: true, message: "상호명을 입력하세요" },
                minLength: {
                  value: 2,
                  message: "상호명은 최소 2글자 이상이어야 합니다",
                },
              })}
            />

            <ValidateInput
              className="w-full"
              label="사업자번호"
              disabled
              placeholder="사업자번호를 입력하세요"
              errorMessage={errors?.businessNumber?.message}
              {...register("businessNumber", {
                required: { value: true, message: "사업자번호를 입력하세요" },
                pattern: {
                  value: /^\d{3}-\d{2}-\d{5}$/,
                  message: "올바른 사업자번호 형식을 입력하세요",
                },
                onChange: (e) => {
                  const formattedValue = formatBusinessInput(e.target.value);
                  setValue("businessNumber", formattedValue);
                },
              })}
            />

            {/* 로그아웃 / 회원탈퇴 */}
            <div className="flex justify-between px-1 pt-2">
              <button
                type="button"
                className="text-sm text-gray-400 text-[#666666]"
                onClick={() => handleLogout()}
              >
                로그아웃
              </button>
              <button
                type="button"
                className="text-sm text-gray-400 text-[#666666]"
                onClick={handleWithdrawalConfirmOpen}
              >
                회원탈퇴
              </button>
            </div>
          </SequentialAnimation>
        </form>

        <div className="px-5 pt-6 pb-6 w-full">
          <Button
            className="w-full bg-[#F5F5F5]"
            style={{ pointerEvents: "auto" }}
            onClick={handleSubmit(bindSubmit)}
            disabled={!isValid || updateMutation.isPending}
          >
            <div className="py-3 mx-auto w-full text-base font-semibold text-center text-white bg-orange-400 rounded-full">
              {updateMutation.isPending ? "저장 중..." : "저장"}
            </div>
          </Button>
        </div>
      </div>

      {/* 저장 결과 메시지 모달 */}
      <Dialog
        visible={isModalVisible}
        onClose={handleModalClose}
        persist={false}
      >
        <div className="flex flex-col gap-1">
          <div className="flex flex-col mb-2">
            <div className="text-lg font-semibold text-center">
              {modalMessage.title}
            </div>
          </div>
          <div className="flex flex-col mb-2">
            <div className="text-sm text-center text-[#666666]">
              {modalMessage.message1}
            </div>
            {modalMessage.message2 && (
              <div className="text-sm text-center text-[#666666]">
                {modalMessage.message2}
              </div>
            )}
            {modalMessage.message3 && (
              <div className="text-sm text-center text-[#666666]">
                {modalMessage.message3}
              </div>
            )}
          </div>
          <div className="flex gap-3 mt-2">
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
              onClick={handleModalClose}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>

      {/* 회원탈퇴 확인 모달 */}
      <Dialog
        visible={isWithdrawalConfirmVisible}
        onClose={handleWithdrawalConfirmClose}
        persist={false}
      >
        <div className="flex flex-col gap-1">
          <div className="flex flex-col mb-2">
            <div className="text-lg font-semibold text-center">
              회원 탈퇴
            </div>
          </div>
          <div className="flex flex-col mb-2">
            <div className="text-sm text-center text-[#666666]">
              회원 탈퇴 시 모든 개인정보 및 사업자 정보를
            </div>
            <div className="text-sm text-center text-[#666666]">
              더이상 확인하실 수 없으며, 정산정보가 삭제됩니다.
            </div>
            <div className="text-sm text-center text-[#666666] mt-2">
              탈퇴 후에는 삭제된 정보를 복구할 수 없습니다.
            </div>
          </div>
          <div className="flex gap-3 mt-2">
            <button
              className="flex-1 py-2 font-semibold text-gray-600 border border-[#E5E5E5] rounded-full"
              onClick={handleWithdrawalConfirmClose}
            >
              취소
            </button>
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
              onClick={handleWithdrawalConfirm}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>

      {/* 회원탈퇴 완료 모달 */}
      <Dialog
        visible={isWithdrawalCompleteVisible}
        onClose={handleWithdrawalCompleteClose}
        persist={false}
      >
        <div className="flex flex-col gap-1">
          <div className="flex flex-col mb-2">
            <div className="text-lg font-semibold text-center">
              회원 탈퇴
            </div>
          </div>
          <div className="flex flex-col mb-2">
            <div className="text-sm text-center text-[#666666]">
              회원 탈퇴가 완료되었습니다.
            </div>
          </div>
          <div className="flex gap-3 mt-2">
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
              onClick={handleWithdrawalCompleteClose}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>

      {/* 회원탈퇴 에러 모달 */}
      <Dialog
        visible={isWithdrawalErrorVisible}
        onClose={handleWithdrawalErrorClose}
        persist={false}
      >
        <div className="flex flex-col gap-1">
          <div className="flex flex-col mb-2">
            <div className="text-lg font-semibold text-center">
              회원 탈퇴 실패
            </div>
          </div>
          <div className="flex flex-col mb-2">
            <div className="text-sm text-center text-[#666666]">
              {withdrawalErrorMessage}
            </div>
          </div>
          <div className="flex gap-3 mt-2">
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
              onClick={handleWithdrawalErrorClose}
            >
              확인
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
